// Viewer for a saved custom report (by id) OR a canned one (by canned id).
// Driven by whether the URL matches /reports/canned/:id vs /reports/:id.
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/page-header";
import { ReportResultView } from "@/components/report-result";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

interface Props { canned?: boolean }

export default function ReportViewPage({ canned }: Props) {
  const { id } = useParams();
  const navigate = useNavigate();
  const endpoint = canned ? `/reports/custom/canned/${id}/run` : `/reports/custom/${id}/run`;

  const { data, isLoading, error } = useQuery({
    queryKey: [endpoint],
    queryFn: async () => (await api.get(endpoint)).data,
    enabled: !!id,
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Running report…</div>;
  if (error || !data) return (
    <div className="p-6">
      <Button variant="outline" onClick={() => navigate("/reports")}><ArrowLeft className="mr-1 h-4 w-4"/>Back</Button>
      <p className="mt-4 text-sm text-destructive">Failed to load report.</p>
    </div>
  );

  return (
    <>
      <PageHeader
        title={data.name}
        description={data.category ? `Category: ${data.category}` : undefined}
        actions={
          <>
            <Button asChild variant="outline"><Link to="/reports"><ArrowLeft className="mr-1 h-4 w-4"/>Back</Link></Button>
            {!canned && (
              <Button asChild><Link to={`/reports/${id}/edit`}>Edit</Link></Button>
            )}
          </>
        }
      />
      <div className="p-6">
        <ReportResultView result={data.result} />
      </div>
    </>
  );
}
